import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, IconButton, InputBase, LinearProgress, Tooltip } from '@material-ui/core';
import { AccountTree, CheckCircle, EmojiEvents, Event, Public, Lock, Search, Close } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { ITournament, TournamentType, TournamentVisibility } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { TournamentsData, TournamentsQuery, TournamentsVars } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { getTeamSize } from '@pvplevelup/core/dist/tournament/TournamentRules';
import { PageEmpty, PageHeader, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { Segmented } from '../Pve/run/PveRunList';
import { TYPE_COLORS, TOURNAMENT_ACCENT, bracketLabel, formatCountdown, formatDateTime, getStatusInfo, typeLabel } from './tournamentFormat';

type When = 'upcoming' | 'live' | 'past';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  search: {
    display: 'inline-grid',
    gridTemplateColumns: '18px minmax(0, 1fr) 26px',
    gridColumnGap: 6,
    alignItems: 'center',
    width: 260,
    height: 36,
    padding: '0 4px 0 12px',
    borderRadius: 18,
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    transition: 'border-color 0.12s, background 0.12s',
    '&:focus-within': {
      borderColor: TOURNAMENT_ACCENT,
      background: 'rgba(255, 179, 0, 0.06)',
    },
  },
  searchInput: {
    color: '#fff',
    fontSize: '0.88rem',
  },
  searching: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.84rem',
    color: PAGE_KIT.muted,
  },
  dimmed: {
    opacity: 0.45,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
    gridGap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  card: {
    position: 'relative',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr auto',
    gridGap: 12,
    padding: theme.spacing(2, 2.25),
    borderRadius: 14,
    background: PAGE_KIT.panel,
    border: PAGE_KIT.border,
    boxShadow: PAGE_KIT.shadow,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: 'rgba(255, 179, 0, 0.35)',
      boxShadow: '0 16px 36px rgba(0, 0, 0, 0.38)',
    },
  },
  accent: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    height: 3,
  },
  cardTop: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: 10,
    alignItems: 'start',
  },
  name: {
    fontSize: '1.08rem',
    fontWeight: 800,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  organizer: {
    marginTop: 3,
    fontSize: '0.8rem',
    color: PAGE_KIT.faint,
  },
  status: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 6,
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 11,
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
    border: '1px solid',
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: 6,
      marginBottom: 6,
    },
  },
  chip: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 4,
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 11,
    fontSize: '0.72rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'rgba(255, 255, 255, 0.07)',
    '& svg': {
      fontSize: 13,
    },
  },
  when: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 10,
    alignItems: 'center',
    fontSize: '0.85rem',
  },
  whenIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: 'grid',
    placeItems: 'center',
    color: TOURNAMENT_ACCENT,
    background: 'rgba(255, 179, 0, 0.12)',
    '& svg': {
      fontSize: 18,
    },
  },
  whenMain: {
    fontWeight: 700,
  },
  whenSub: {
    fontSize: '0.78rem',
    color: PAGE_KIT.faint,
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: 12,
    alignItems: 'center',
    paddingTop: 12,
    borderTop: PAGE_KIT.divider,
  },
  fill: {
    height: 5,
    borderRadius: 3,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  fillBar: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  fillText: {
    marginTop: 5,
    fontSize: '0.76rem',
    color: PAGE_KIT.muted,
  },
  entered: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 5,
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#81c784',
    '& svg': {
      fontSize: 15,
    },
  },
  more: {
    display: 'grid',
    placeItems: 'center',
    marginTop: theme.spacing(2),
  },
}));

interface Props {
  /** where a tournament's page is (desktop and the website both use /tournaments) */
  basePath?: string;
  /** buttons on the right of the header, e.g. Create tournament */
  actions?: React.ReactNode;
}

const WHEN_EMPTY: Record<When, {title: string, text: string}> = {
  upcoming: {title: 'No upcoming tournaments', text: 'New tournaments show up here as soon as an organizer opens sign-ups.'},
  live: {title: 'Nothing live right now', text: 'Tournaments that are under way show up here, with their brackets.'},
  past: {title: 'No past tournaments yet', text: 'Finished tournaments and their results will be kept here.'},
};

/** A card per tournament: what it is, when, and how full it is. */
export const TournamentCard: React.FC<{tournament: ITournament, onOpen: () => void}> = ({tournament: t, onOpen}) => {
  const classes = useStyles();
  const status = getStatusInfo(t.status);
  const color = TYPE_COLORS[t.type] || TOURNAMENT_ACCENT;
  const teamSize = getTeamSize(t);
  const count = t.participantCount || 0;
  const target = t.maxParticipants || t.minParticipants || 1;
  const pct = Math.min(100, Math.round((count / target) * 100));
  const enoughPlayers = count >= (t.minParticipants || 0);
  return (
    <div className={classes.card} onClick={onOpen} role="button">
      <span className={classes.accent} style={{background: `linear-gradient(90deg, ${color}, ${TOURNAMENT_ACCENT})`}} />
      <div className={classes.cardTop}>
        <div style={{minWidth: 0}}>
          <div className={classes.name} title={t.name}>{t.name}</div>
          <div className={classes.organizer}>by {(t.organizer as any)?.displayname || 'unknown'}</div>
        </div>
        <span className={classes.status} style={{color: status.color, borderColor: `${status.color}66`, background: `${status.color}1a`}}>
          <span className={classes.dot} style={{background: status.color}} />{status.label}
        </span>
      </div>
      <div className={classes.chips}>
        <span className={classes.chip} style={{color, background: `${color}1f`}}>{typeLabel(t.type)}</span>
        <span className={classes.chip}><AccountTree />{bracketLabel(t.bracket)}</span>
        <span className={classes.chip}><Public />{t.server}</span>
        {t.type != TournamentType.Dueling && <span className={classes.chip}>{t.type == TournamentType.PvE ? `Teams of ${teamSize}` : `${teamSize}v${teamSize}`}</span>}
        {t.visibility == TournamentVisibility.InviteOnly && <span className={classes.chip}><Lock />Invite only</span>}
      </div>
      <div className={classes.when}>
        <span className={classes.whenIcon}><Event /></span>
        <div style={{minWidth: 0}}>
          <div className={classes.whenMain}>{formatDateTime(t.startAt)}</div>
          <div className={classes.whenSub}>
            {t.status == 'Completed' ? (t.championName ? <span><EmojiEvents style={{fontSize: 13, verticalAlign: -2, marginRight: 3, color: TOURNAMENT_ACCENT}} />Won by {t.championName}</span> : 'Finished')
              : t.status == 'Cancelled' ? 'Cancelled' : t.status == 'Running' || t.status == 'Forming' ? 'Under way'
              : t.status == 'SignupsOpen' ? `Sign-ups close ${formatCountdown(t.signupCloseAt)}` : `Starts ${formatCountdown(t.startAt)}`}
          </div>
        </div>
      </div>
      <div className={classes.footer}>
        <div>
          <div className={classes.fill}>
            <div className={classes.fillBar} style={{width: `${pct}%`, background: enoughPlayers ? 'linear-gradient(90deg, #43a047, #66bb6a)' : `linear-gradient(90deg, ${TOURNAMENT_ACCENT}, #ffca28)`}} />
          </div>
          <div className={classes.fillText}>
            {count} {count == 1 ? 'player' : 'players'}{t.maxParticipants ? ` of ${t.maxParticipants}` : ''}
            {!enoughPlayers ? ` · ${(t.minParticipants || 0) - count} more needed` : ''}
          </div>
        </div>
        {t.myEntry ? <Tooltip title="You're signed up"><span className={classes.entered}><CheckCircle />Signed up</span></Tooltip> : <span />}
      </div>
    </div>
  );
}

/** Tournaments, upcoming / live / past, filtered by server and type. Used by the app and the website. */
export const TournamentList: React.FC<Props> = ({basePath = '/tournaments', actions}) => {
  const classes = useStyles();
  const page = usePageStyles();
  const history = useHistory();
  const [when, setWhen] = useState<When>('upcoming');
  const [server, setServer] = useState<string>('');
  const [type, setType] = useState<string>('');
  // the search box (typing) and the search sent (after a short pause)
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), 300);
    return () => clearTimeout(timer);
  }, [typed]);
  const clearSearch = () => { setTyped(''); setSearch(''); };

  const variables: TournamentsVars = {when, server: server || null, type: type || null, next: null, search: search || null};
  const { data, loading, fetchMore } = useQuery<TournamentsData, TournamentsVars>(TournamentsQuery, {variables, fetchPolicy: 'cache-and-network'});
  const tournaments = data?.tournaments?.tournaments || [];

  const loadMore = () => {
    if(!data?.tournaments?.hasNext || loading)
      return;
    fetchMore({
      variables: {...variables, next: data.tournaments.next},
      updateQuery: (prev, {fetchMoreResult}) => !fetchMoreResult ? prev : {
        ...prev,
        tournaments: {
          ...fetchMoreResult.tournaments,
          tournaments: _.uniqBy([...prev.tournaments.tournaments, ...fetchMoreResult.tournaments.tournaments], (t) => String(t._id)),
        },
      },
    });
  };

  return (
    <div>
      <PageHeader icon={<EmojiEvents />} title="Tournaments" childrenStyle="filters" actions={actions}
        subtitle="Group PvP, dueling and dungeon cups. Sign up, form your team and climb the bracket.">
        <div className={classes.filters}>
          <div className={classes.search}>
            <Search style={{fontSize: 18, color: PAGE_KIT.faint}} />
            <InputBase className={classes.searchInput} value={typed} placeholder="Search tournaments" inputProps={{maxLength: 60, 'aria-label': 'Search tournaments'}}
              onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if(e.key == 'Escape') clearSearch(); }} />
            {typed ? <IconButton size="small" onClick={clearSearch} aria-label="Clear the search"><Close style={{fontSize: 16}} /></IconButton> : <span />}
          </div>
          {/* a search covers every tab; picking a tab ends it */}
          <span className={search ? classes.dimmed : undefined}>
            <Segmented value={when} onChange={(v: When) => { clearSearch(); setWhen(v); }} options={[
              {value: 'upcoming' as When, label: 'Upcoming'},
              {value: 'live' as When, label: 'Live'},
              {value: 'past' as When, label: 'Past'},
            ]} />
          </span>
          <Segmented value={server} onChange={setServer} options={[
            {value: '', label: 'All servers', icon: <Public />},
            {value: 'Fury', label: 'Fury'},
            {value: 'Crom', label: 'Crom'},
          ]} />
          <Segmented value={type} onChange={setType} options={[
            {value: '', label: 'All types'},
            {value: TournamentType.GroupPvP, label: typeLabel(TournamentType.GroupPvP)},
            {value: TournamentType.Dueling, label: typeLabel(TournamentType.Dueling)},
            {value: TournamentType.PvE, label: typeLabel(TournamentType.PvE)},
          ]} />
        </div>
      </PageHeader>
      {search &&
        <div className={classes.searching}>
          {loading && tournaments.length == 0 ? 'Searching' : `${tournaments.length}${data?.tournaments?.hasNext ? '+' : ''} ${tournaments.length == 1 ? 'tournament' : 'tournaments'}`} named like "<b>{search}</b>" · upcoming, live and past
        </div>}
      {loading && tournaments.length == 0 && <LinearProgress style={{marginTop: 16, height: 2}} />}
      {tournaments.length > 0 &&
        <div className={classes.grid}>
          {tournaments.map((t) => <TournamentCard key={String(t._id)} tournament={t} onOpen={() => history.push(`${basePath}/${t._id}`)} />)}
        </div>
      }
      {data?.tournaments?.hasNext &&
        <div className={classes.more}>
          <Button className={page.secondaryButton} onClick={loadMore} disabled={loading}>Load more</Button>
        </div>
      }
      {!loading && tournaments.length == 0 &&
        <div className={clsx(page.panel)} style={{marginTop: 16}}>
          {search
            ? <PageEmpty icon={<Search />} title="No tournaments found" text={`Nothing named like "${search}"${server || type ? ' with these filters' : ''}. Try another name.`}
                action={<Button className={page.secondaryButton} onClick={clearSearch}>Clear the search</Button>} />
            : <PageEmpty icon={<EmojiEvents />} title={WHEN_EMPTY[when].title} text={WHEN_EMPTY[when].text} />}
        </div>
      }
    </div>
  );
}
